import { ChangeEvent, FormEvent, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import AdminShell from "@/components/admin/AdminShell";
import {
  JOURNAL_CATEGORIES,
  JournalPost,
  deletePost,
  getPostById,
  savePost,
  slugify,
  uploadJournalImage,
} from "@/lib/journalApi";

type Draft = Partial<JournalPost>;

const empty: Draft = {
  title: "",
  slug: "",
  excerpt: "",
  body: "",
  category: "Behind the Music",
  cover_image_url: "",
  cover_alt: "",
  seo_title: "",
  seo_description: "",
  author: "Olivia Flemming",
  status: "draft",
  featured: false,
  published_at: null,
};

const field = "w-full rounded-xl bg-black/40 border border-white/15 px-4 py-3 text-base text-white focus:outline-none focus:border-primary";
const label = "block text-xs uppercase tracking-[0.2em] text-white/50 mb-2 font-inter";

const JournalEdit = () => {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();
  const [post, setPost] = useState<Draft>(empty);
  const [loading, setLoading] = useState(!isNew);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [slugTouched, setSlugTouched] = useState(!isNew);

  useEffect(() => {
    if (isNew) return;
    getPostById(id!).then((p) => { if (p) setPost(p); setLoading(false); });
  }, [id, isNew]);

  const set = (k: keyof JournalPost, v: unknown) => setPost((p) => ({ ...p, [k]: v }));

  const onTitle = (v: string) => {
    set("title", v);
    if (!slugTouched) set("slug", slugify(v));
  };

  const onImage = async (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true); setMsg("Uploading photo…");
    try { set("cover_image_url", await uploadJournalImage(f)); setMsg("Photo added."); }
    catch (err: any) { setMsg(err.message); }
    finally { setBusy(false); }
  };

  const save = async (status?: "draft" | "published") => {
    if (!post.title?.trim() || !post.slug?.trim()) { setMsg("Add a title and web address first."); return; }
    setBusy(true); setMsg(null);
    try {
      const next: Draft = { ...post, slug: slugify(post.slug!) };
      if (status) next.status = status;
      if (next.status === "published" && !next.published_at) next.published_at = new Date().toISOString();
      const saved = await savePost(next);
      setPost(saved);
      setMsg(saved.status === "published" ? "Saved. This article is live." : "Saved as a draft.");
      if (isNew) navigate(`/admin/journal/${saved.id}`, { replace: true });
    } catch (err: any) {
      setMsg(err.message?.includes("duplicate") ? "Another article already uses that web address." : err.message);
    } finally { setBusy(false); }
  };

  const onSubmit = (e: FormEvent) => { e.preventDefault(); save(); };

  const onDelete = async () => {
    if (!post.id || !confirm("Delete this article for good?")) return;
    await deletePost(post.id);
    navigate("/admin/journal");
  };

  if (loading) return <AdminShell title="Article"><p className="text-white/50">Loading…</p></AdminShell>;

  const live = post.status === "published";

  return (
    <AdminShell
      title={isNew ? "New article" : "Edit article"}
      actions={<div><Link to="/admin/journal" className="text-sm text-white/60 hover:text-white">← All articles</Link></div>}
    >
      <form onSubmit={onSubmit} className="grid lg:grid-cols-[1fr_320px] gap-8">
        <div className="space-y-5">
          <div><label className={label}>Title</label><input className={field} value={post.title ?? ""} onChange={(e) => onTitle(e.target.value)} /></div>
          <div>
            <label className={label}>Web address</label>
            <div className="flex items-center gap-2">
              <span className="text-white/40 text-sm shrink-0">/journal/</span>
              <input className={field} value={post.slug ?? ""} onChange={(e) => { setSlugTouched(true); set("slug", e.target.value); }} />
            </div>
          </div>
          <div><label className={label}>Introduction</label><textarea rows={3} className={field} value={post.excerpt ?? ""} onChange={(e) => set("excerpt", e.target.value)} /></div>
          <div>
            <label className={label}>Article</label>
            <textarea rows={22} className={`${field} font-mono text-sm leading-relaxed`} value={post.body ?? ""} onChange={(e) => set("body", e.target.value)} />
            <p className="text-xs text-white/40 mt-2">Start a line with ## for a heading, ### for a smaller heading, - for a list. Links: [words](/weddings).</p>
          </div>
        </div>

        <aside className="space-y-5">
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 space-y-3">
            <p className="text-sm">Status: <span className={live ? "text-primary" : "text-white/60"}>{live ? "Live" : "Draft"}</span></p>
            <button type="button" disabled={busy} onClick={() => save("draft")} className="w-full rounded-full border border-white/20 py-2.5 text-sm hover:border-white/40">
              {live ? "Unpublish (save as draft)" : "Save draft"}
            </button>
            <button type="button" disabled={busy} onClick={() => save("published")} className="w-full rounded-full bg-primary text-primary-foreground py-2.5 text-sm">
              {live ? "Save changes" : "Publish"}
            </button>
            {live && post.slug && <a href={`/journal/${post.slug}`} target="_blank" rel="noreferrer" className="block text-center text-xs text-white/50 underline">View article</a>}
            {msg && <p className="text-xs text-white/70">{msg}</p>}
          </div>

          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 space-y-4">
            <div>
              <label className={label}>Category</label>
              <select className={field} value={post.category} onChange={(e) => set("category", e.target.value)}>
                {JOURNAL_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <label className="flex items-center gap-3 text-sm">
              <input type="checkbox" checked={!!post.featured} onChange={(e) => set("featured", e.target.checked)} className="w-5 h-5" />
              Feature at the top of the Journal
            </label>
            <div><label className={label}>Publish date</label><input type="date" className={field} value={post.published_at?.slice(0, 10) ?? ""} onChange={(e) => set("published_at", e.target.value ? `${e.target.value}T12:00:00Z` : null)} /></div>
            <div><label className={label}>Author</label><input className={field} value={post.author ?? ""} onChange={(e) => set("author", e.target.value)} /></div>
          </div>

          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 space-y-4">
            <label className={label}>Photo</label>
            {post.cover_image_url && <img src={post.cover_image_url} alt="" className="w-full aspect-[4/3] object-cover rounded-lg" />}
            <input type="file" accept="image/*" onChange={onImage} className="text-sm text-white/60" />
            <div><label className={label}>Photo description</label><input className={field} value={post.cover_alt ?? ""} onChange={(e) => set("cover_alt", e.target.value)} placeholder="What the photo shows" /></div>
          </div>

          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 space-y-4">
            <p className="text-sm text-white/70">Google listing</p>
            <div><label className={label}>Search title</label><input className={field} value={post.seo_title ?? ""} onChange={(e) => set("seo_title", e.target.value)} placeholder={post.title} /><p className="text-[11px] text-white/35 mt-1">{(post.seo_title || post.title || "").length}/60</p></div>
            <div><label className={label}>Search description</label><textarea rows={3} className={field} value={post.seo_description ?? ""} onChange={(e) => set("seo_description", e.target.value)} placeholder={post.excerpt} /><p className="text-[11px] text-white/35 mt-1">{(post.seo_description || "").length}/160</p></div>
          </div>

          {!isNew && <button type="button" onClick={onDelete} className="w-full text-xs text-red-300/80 hover:text-red-300">Delete article</button>}
        </aside>
      </form>
    </AdminShell>
  );
};

export default JournalEdit;
